import Vue from 'vue'
import axios from 'axios'
import { httpHelper, trackerHelper } from '@/utils'

// Preserve newer live fields during a resync.
const LIVE_SHARED_TRACKER_PATHS = ['last_contact', 'position', 'sensor_data']

const state = {
    activeSharedTracker: null,
    activeSharedTrackerTrace: [],
    sharingToken: null,
    isSharingTokenInvalid: false,
    sharedTrackers: [],
    sharingConfig: null,
}

const getters = {}

const actions = {
    batchUpdateSharedTrackerLive({ state }, updates) {
        const updatedIndexes = new Set()
        updates.forEach(({ tracker: id, ...data }) => {
            const index = state.sharedTrackers.findIndex(
                tracker => tracker.id === id
            )
            const tracker = state.sharedTrackers[index]

            if (!tracker) {
                return
            }

            tracker.last_contact = data.timestamp

            if ('lat' in data && 'long' in data) {
                tracker.position.lat = data.lat
                tracker.position.lng = data.long
                tracker.position.timestamp = data.timestamp

                if (state.activeSharedTracker?.id === id) {
                    state.activeSharedTrackerTrace = [
                        [data.lat, data.long],
                        ...state.activeSharedTrackerTrace,
                    ]
                }
            }

            if ('values' in data) {
                Object.entries(data.values).forEach(([key, value]) => {
                    tracker.sensor_data[key] = {
                        value,
                        last_update: data.timestamp,
                    }
                })
            }

            updatedIndexes.add(index)
        })
        updatedIndexes.forEach(index => {
            Vue.set(state.sharedTrackers, index, state.sharedTrackers[index])
        })
    },
    // Restore shared assets after a websocket gap.
    async resyncSharedTrackers({ state, commit }) {
        if (!state.sharingToken || !state.sharingConfig) {
            return
        }

        const { data } = await httpHelper.get('/share/assets/', {
            params: { token: state.sharingToken },
        })

        const { hasMembershipChanged } = trackerHelper.mergeTrackersInPlace(
            state.sharedTrackers,
            data.results,
            {
                timestampField: 'last_contact',
                livePaths: LIVE_SHARED_TRACKER_PATHS,
            }
        )

        const activeId = state.activeSharedTracker?.id

        if (
            hasMembershipChanged &&
            activeId &&
            !state.sharedTrackers.some(({ id }) => id === activeId)
        ) {
            commit('setActiveSharedTracker', null)
        }
    },
    async loadSharingData({ commit }, { token }) {
        try {
            commit('setSharingToken', token)
            commit('setSharingTokenInvalid', false)
            commit('common/setDataLoading', true, { root: true })
            // query the share config on all backends
            const api_urls = process.env.VUE_APP_API_URL.split(',')
            const jwt_urls = process.env.VUE_APP_JWT_API_URL.split(',')
            const websocket_urls = process.env.VUE_APP_WEBSOCKET_URL.split(',')

            const requests = api_urls.map(url =>
                axios
                    .get(new URL('v1/share/config/', url).toString(), {
                        params: { token },
                    })
                    .then(response => ({
                        url,
                        response,
                    }))
            )
            // keep the first successful backend
            const {
                response: configResponse,
                url: backend_url,
            } = await Promise.any(requests)
            // find the websocket url for that backend and update the state
            for (let i = 0; i < api_urls.length; i++) {
                if (api_urls[i].startsWith(backend_url)) {
                    const backend_dict = {
                        api_url: api_urls[i],
                        jwt_url: jwt_urls[i],
                        websocket_url: websocket_urls[i],
                    }
                    localStorage.setItem(
                        'backend',
                        JSON.stringify(backend_dict)
                    )
                    commit('auth/setBackend', backend_dict, { root: true })
                    break
                }
            }
            // now that the backend is identified use the regular http helper for the /share/assets
            const assetsResponse = await httpHelper.get('/share/assets/', {
                params: { token },
            })
            commit('setSharingConfig', configResponse.data)
            commit('setSharedTrackers', assetsResponse.data.results)
        } catch {
            commit('setSharingTokenInvalid', true)
            commit('setSharingConfig', null)
            commit('setSharedTrackers', [])
        } finally {
            commit('common/setDataLoading', false, { root: true })
        }
    },
}

const mutations = {
    setActiveSharedTracker(state, data) {
        if (!data) {
            state.activeSharedTrackerTrace = []
        } else if (data.id !== state.activeSharedTracker?.id) {
            state.activeSharedTrackerTrace = [
                [data.position.lat, data.position.lng],
            ]
        }

        state.activeSharedTracker = data
    },
    setSharingConfig(state, data) {
        state.sharingConfig = data
    },
    setSharingToken(state, data) {
        state.sharingToken = data
    },
    setSharingTokenInvalid(state, data) {
        state.isSharingTokenInvalid = data
    },
    setSharedTrackers(state, data) {
        state.sharedTrackers = data
    },
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}
